angular
  .module('acme.view.application.reports.result.controllers', [
    'ui.router',
    'ui.bootstrap',
    'acme.common.directive.dataGrid',
    'acme.common.service.reportingApi',
    'acme.common.service.calendar',
    'acme.common.service.userPreferences',
  ])
  .controller('ReportsResultController', ReportsResultController);

/**
 * Controller responsible for managing Result View
 * @class ReportsResultController
 * @constructor
 * @param {Object} $state - ui-router $state service
 * @param {Object} $q - angular's $q service
 * @param {Object} $rootScope - angular's $rootScope service
 * @param {Object} $filter - angular's $filter service
 * @param {Object} HelpersService - acme's HelpersService
 * @param {Object} ReportingApiService - acme's ReportingApiService
 * @param {Object} UserPreferencesService - acme's ReportingApiService
 * @param {Object} CalendarService - acme's CalendarService
 * @return {ReportsResultController} - Class instance
 */
function ReportsResultController(
  $state,
  $q,
  $rootScope,
  $filter,
  HelpersService,
  ApiService,
  UserPreferencesService,
  CalendarService,
) {
  Object.assign(this, {
    $state: $state,
    $q: $q,
    $rootScope: $rootScope,
    $filter: $filter,
    HelpersService: HelpersService,
    ApiService: ApiService,
    UserPreferencesService: UserPreferencesService,
    CalendarService: CalendarService,
  });
  // TODO: update ui-router to 0.3.0 to avoid calling this manually
  this.$onInit();
}

/**
 * Angular lifecycle method $onInit
 * @public
 * @memberOf ReportsResultController
 * @return {Undefined} -
 */
ReportsResultController.prototype.$onInit = function () {
  var id = this.$state.params.resultId;

  if (!id) {
    this.$state.go('application.reports.results');
    return;
  }

  this.report = {
    id: id,
    reportJson: this.$state.params.reportJson,
    reportStatus: this.$state.params.reportStatus,
  };
  this._setSummaryFields();
  this._load();
  this._fireEvents();
};

/**
 * Reponsible for returning some field transformation functions
 * @memberOf ReportsResultController
 * @private
 * @return {Object} - Object containing a function transformation on each key
 */
ReportsResultController.prototype._getFieldTransforms = function () {
  var HelpersService = this.HelpersService,
    UserPreferencesService = this.UserPreferencesService,
    CalendarService = this.CalendarService,
    filter = this.$filter;

  return {
    DATE: function (data) {
      var date = new Date(data),
        dataString = data.toString(),
        dateTimeFormat = UserPreferencesService.getDateTimeFormat('shortDateTime');

      if (dataString.length < 6) {
        // in case of a partial date we skip transforming it
        return data;
      }

      return (date.valueOf() && filter('date')(CalendarService.parseDate(date), dateTimeFormat)) || '';
    },
  };
};

/**
 * Sets status and groupings
 * @memberOf ReportsResultController
 * @private
 * @return {Undefined} -
 */
ReportsResultController.prototype._setSummaryFields = function () {
  var dateTransform = this._getFieldTransforms().DATE;

  this.status = [
    {
      display: 'Date Completed',
      transform: dateTransform,
      key: 'updatedOn',
    },
    {
      display: 'Date Field',
      key: 'dateRangeField',
    },
    {
      display: 'From',
      transform: dateTransform,
      key: 'startDate',
    },
    {
      display: 'To',
      transform: dateTransform,
      key: 'endDate',
    },
    {
      display: 'Requested By',
      key: 'createdBy',
    },
  ];
  this.groupings = [
    {
      key: 'count',
      field: true,
    },
    {
      key: 'group',
      field: true,
    },
    {
      key: 'summary',
      field: true,
    },
    {
      key: 'find',
      field: false,
    },
  ].map(function (item) {
    return {
      display: item.key,
      key: item.field ? item.key + 'Fields' : item.key + 'Queries',
    };
  });
};

/**
 * Formats a single group line
 * @memberOf ReportsResultController
 * @param {String} name - name of the group
 * @param {Object} data - Field group data
 * @public
 * @return {String} - formatted data
 */
ReportsResultController.prototype.formatGroupingsLine = function (name, data) {
  var fieldName = data.fieldName,
    groupingFunction = data[name + 'Function'],
    operator = data.operator,
    value = data.fieldValue;

  groupingFunction = groupingFunction ? 'ƒ ' + groupingFunction : '';

  return [fieldName, groupingFunction, operator, value]
    .filter(function (item) {
      return !!item;
    })
    .join(' - ');
};

/**
 * Sets cookies and opens the print app
 * @memberOf ReportsResultController
 * @param {Object} reportStatus - async report status data
 * @private
 * @return {Undefined} -
 */
ReportsResultController.prototype._print = function (reportStatus) {
  var _reportStatus = reportStatus || {},
    wHeight = screen.availHeight - 20,
    wWidth = screen.availWidth - 20;

  this.HelpersService.setCookie('report', {
    endDate: _reportStatus.endDate,
    startDate: _reportStatus.startDate,
    dateRangeField: _reportStatus.dateRangeField,
    queryExpression: _reportStatus.queryExpression,
    name: _reportStatus.reportName,
    description: _reportStatus.description,
    id: _reportStatus.id,
    async: true,
  });

  window.open(
    '/app/reports/print',
    _reportStatus.reportName,
    'height=' + wHeight + ',width=' + wWidth + ',left=10,scrollbars=yes,resizable=yes',
  );
  return;
};

/**
 * Loads async report status and json,
 * unless they're already passed as arguments
 * And updates the view
 * @memberOf ReportsResultController
 * @private
 * @return {Undefined} -
 */
ReportsResultController.prototype._load = function () {
  var id = this.report.id,
    api = this.ApiService.reporting.asyncReports,
    reportStatus = this.report.reportStatus,
    reportJson = this.report.reportJson,
    handleAsyncReportResponse = this._handleAsyncReportResponse.bind(this),
    getVm = this._getVm.bind(this),
    updateView = this._updateView.bind(this);

  this.loading = true;
  return this.$q
    .all([this._fetchData(id, api.getJson.bind(api), reportJson), this._fetchData(id, api.get.bind(api), reportStatus)])
    .then(handleAsyncReportResponse)
    .then(getVm)
    .then(updateView)
    .catch(updateView);
};

/**
 * Process the ViewModel based on the passed data
 * @memberOf ReportsResultController
 * @param {{reportStatus: Object, jsonResult: Object}} data - data to compute the vm
 * @private
 * @return {Object} - the viewModel
 */
ReportsResultController.prototype._getVm = function (data) {
  var _data = data || {},
    reportStatus = _data.reportStatus,
    jsonResult = _data.jsonResult,
    csvUrl = this.ApiService.reporting.asyncReports.csvUrl,
    columns = null,
    downloadCsv = null,
    groupings = null,
    lines = null,
    print = null,
    processedFields = null,
    status = null,
    uuid = null;

  downloadCsv = [csvUrl, reportStatus.id].join('/');
  processedFields = this._processResultFields(jsonResult.resultFieldList, this._getFieldTransforms());
  groupings = this._fieldProcessor(this.groupings, reportStatus.queryExpression);
  status = this._fieldProcessor(this.status, reportStatus);
  print = this._print.bind(this, reportStatus);
  uuid = reportStatus.reportUuid;

  columns = this._buildColumns(processedFields.columnNames);
  lines = this._buildLines(processedFields.lineRecords);

  return {
    columns: columns,
    downloadCsv: downloadCsv,
    groupings: groupings,
    hasData: lines.length,
    lines: lines,
    print: print,
    reportName: reportStatus.reportName,
    showSummary: status.length || groupings.length,
    status: status,
    uuid: uuid,
  };
};

/**
 * Normalizes resultsFields from the json data
 * @memberOf ReportsResultController
 * @param {Object} resultFields - resultsFields from the async json data
 * @param {Object=} transformations - Object containing transformations per dataType
 * @private
 * @return {{columnNames: Array, lineRecords: Array}} - normalized data
 */
ReportsResultController.prototype._processResultFields = function (resultFields, transformations) {
  var _resultFields = resultFields || [],
    _transformations = transformations || {};

  return _resultFields.reduce(function (result, field) {
    var columnNames = result.columnNames || [],
      transform = _transformations[field.analyticsDataType],
      transformedValues = field.values,
      lineRecords = result.lineRecords || [];

    transformedValues = transform ? transformedValues.map(transform) : transformedValues;

    return {
      columnNames: columnNames.concat(field.fieldName),
      lineRecords: lineRecords.concat([transformedValues]),
    };
  }, {});
};

/**
 * Broadcasts required events
 * @memberOf ReportsResultController
 * @private
 * @return {Undefined} -
 */
ReportsResultController.prototype._fireEvents = function () {
  this.$rootScope.$broadcast('Application::contentTitleChange', {
    title: 'Reports',
    buttonLeft: {
      label: 'Back',
      action: function () {
        window.history.go(-1);
      },
    },
  });
};

/**
 * Process columns names to be sent to ag-grid
 * @memberOf ReportsResultController
 * @param {Array.<String>} columnNames - array containing the names of the columns to be displayed
 * @private
 * @return {Array.<{displayName: String, field: String}>} - Array of ag-grid column definitions
 */
ReportsResultController.prototype._buildColumns = function (columnNames) {
  var _columnNames = columnNames || [];
  return _columnNames.map(function (columnName, index) {
    return {
      displayName: columnName,
      field: index.toString(),
    };
  });
};

/**
 * Process row lines to be sent to ag-grid
 * @memberOf ReportsResultController
 * @param {Array.<Array>} lineRecords - row values.
 * @private
 * @return {Array.<Array>} - ag-grid row values format
 */
ReportsResultController.prototype._buildLines = function (lineRecords) {
  var _lineRecords = lineRecords || [];

  return _lineRecords.length ? this.HelpersService.utils.zip.apply(null, _lineRecords) : [];
};

/**
 * Prepare data to be displayed
 * @memberOf ReportsResultController
 * @param {{transform: Function, display: String, key: String}} fieldDescriptors - fields that we are interested in
 * @param {Object} fieldData - data for those fields
 * @private
 * @return {Array.<{id: Number, display: String, data: Array}>} - data to be presented
 */
ReportsResultController.prototype._fieldProcessor = function (fieldDescriptors, fieldData) {
  var _fieldDescriptors = fieldDescriptors || [],
    _fieldData = fieldData || {};

  return _fieldDescriptors
    .map(function (field, id) {
      var transform = field.transform,
        display = field.display,
        data = _fieldData[field.key];

      return {
        id: id,
        display: display,
        data: transform ? transform(data) : data,
      };
    })
    .filter(function (item) {
      return item.data && item.data.length;
    });
};

/**
 * Executes the action if the payload if not passed with the id
 * Otherwise simply returns the payload
 * @memberOf ReportsResultController
 * @param {Number} id - the id of the report
 * @param {Function.Promise} action - action to be executed if the payload if not present
 * @param {Object=} payload - payload containing the reponse
 * @private
 * @return {Promise|Object} - promise from the action of the payload
 */
ReportsResultController.prototype._fetchData = function (id, action, payload) {
  return payload ? payload : action(id);
};

/**
 * Returns the object to compute the viewModel
 * @memberOf ReportsResultController
 * @param {Array} response - reponses from the array of promises
 * @private
 * @return {{jsonResult: Object, reportStatus: Object}} - Api reponses
 */
ReportsResultController.prototype._handleAsyncReportResponse = function (response) {
  return {
    jsonResult: JSON.parse(response[0]),
    reportStatus: response[1],
  };
};

/**
 * Updates the viewModel
 * @memberOf ReportsResultController
 * @param {Object} vm - Object containing the data to update the public vm property
 * @private
 * @return {Undefined} -
 */
ReportsResultController.prototype._updateView = function (vm) {
  this.vm = Object.assign({}, this.vm, vm);
  this.loading = false;
};

ReportsResultController.$inject = [
  '$state',
  '$q',
  '$rootScope',
  '$filter',
  'HelpersService',
  'ApiService',
  'UserPreferencesService',
  'CalendarService',
];
